import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { AuthState } from '../types';
import { authService } from '../services/AuthService';
import { userService } from '../services/UserService';

interface AuthContextType extends AuthState {
  login: (loginId: string, password: string, deviceType: string, deviceId: string) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (guestToken: string, data: {
    login_name: string;
    password: string;
    check_number: string;
    login_id: string;
    mail_address: string;
    device_type: string;
    device_id: string;
  }) => Promise<void>;
  updateProfile: (data: {
    name?: string;
    email?: string;
    password?: string;
  }) => Promise<void>;
  sendVerificationCode: (email: string) => Promise<void>;
  checkVerificationCode: (email: string, code: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    guestToken: null,
    isAuthenticated: false,
    isLoading: true,
    error: ''
  });

  // 초기 게스트 토큰 및 사용자 정보 로드
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setState(prev => ({ ...prev, isLoading: true }));
        
        // 로컬 스토리지에서 토큰 가져오기
        const storedToken = localStorage.getItem('authToken');
        const storedGuestToken = localStorage.getItem('guestToken');
        
        // 게스트 토큰이 없으면 새로 요청
        let guestToken = storedGuestToken;
        if (!guestToken) {
          guestToken = await authService.getGuestToken();
          localStorage.setItem('guestToken', guestToken);
        }
        
        // 인증 토큰이 있으면 사용자 정보 가져오기
        if (storedToken) {
          try {
            const user = await userService.getUserInfo(storedToken);
            setState({
              user: { ...user, token: storedToken },
              guestToken,
              isAuthenticated: true,
              isLoading: false,
              error: ''
            });
          } catch (error) {
            // 토큰이 유효하지 않은 경우
            localStorage.removeItem('authToken');
            setState({
              user: null,
              guestToken,
              isAuthenticated: false,
              isLoading: false,
              error: '세션이 만료되었습니다. 다시 로그인해주세요.'
            });
          }
        } else {
          // 인증 토큰이 없는 경우
          setState({
            user: null,
            guestToken,
            isAuthenticated: false,
            isLoading: false,
            error: ''
          });
        }
      } catch (error) {
        // 인증 초기화 실패 - 앱 시작 시 인증 상태 설정 문제 진단을 위해 로그 유지
        console.error('인증 초기화 실패:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: '인증 초기화에 실패했습니다.'
        }));
      }
    };

    initializeAuth();
  }, []);

  // 로그인 함수
  const login = useCallback(async (loginId: string, password: string, deviceType: string, deviceId: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: '' }));

      const guestToken = localStorage.getItem('guestToken');
      if (!guestToken) return;

      // 로그인 토큰 받기
      const token = await authService.getTokenByLoginId(guestToken, loginId, password, deviceType, deviceId);
      
      // 토큰 저장
      localStorage.setItem('authToken', token);
      
      // 토큰으로 사용자 정보 조회
      const user = await userService.getUserInfo(token);
      
      setState(prev => ({
        user: { ...user, token }, // 토큰도 함께 저장
        guestToken: prev.guestToken,
        isAuthenticated: true,
        isLoading: false,
        error: ''
      }));
    } catch (error) {
      console.error('로그인 실패:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : '로그인에 실패했습니다.'
      }));
      throw error;
    }
  }, []);

  // 로그아웃 함수
  const logout = useCallback(async () => 
  {
    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      // 서버 API 호출 제거 - 클라이언트 측에서만 로그아웃 처리
      
      // 로컬 스토리지에서 토큰 제거
      localStorage.removeItem('authToken');
      
      // 상태 업데이트
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: ''
      }));
    } 
    catch (error) {
      console.error('로그아웃 처리 중 오류:', error);
      
      // 오류가 발생해도 토큰 제거 및 상태 업데이트 시도
      localStorage.removeItem('authToken');
      
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: '로그아웃 처리 중 오류가 발생했습니다.'
      }));
    }
  }, []);

  // 회원가입 함수
  const signUp = useCallback(async (guestToken: string, data: {
    login_name: string;
    password: string;
    check_number: string;
    login_id: string;
    mail_address: string;
    device_type: string;
    device_id: string;
  }) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: '' }));
      
      const user = await authService.signUp(guestToken, data);
      
      // 토큰 저장
      localStorage.setItem('authToken', user.token);
      
      setState({
        user,
        guestToken: state.guestToken,
        isAuthenticated: true,
        isLoading: false,
        error: ''
      });
    } catch (error) {
      // 회원가입 실패 - 사용자 등록 문제 진단을 위해 로그 유지
      console.error('회원가입 실패:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : '회원가입에 실패했습니다.'
      }));
      throw error;
    }
  }, [state.guestToken]);

  // 프로필 업데이트 함수
  const updateProfile = useCallback(async (data: {
    name?: string;
    email?: string;
    password?: string;
  }) => {
    try {
      if (!state.user?.token) {
        throw new Error('인증되지 않은 사용자입니다.');
      }
      
      setState(prev => ({ ...prev, isLoading: true, error: '' }));
      
      const updatedUser = await authService.updateProfile(state.user.token, data);
      
      setState(prev => ({
        ...prev,
        user: updatedUser,
        isLoading: false,
        error: ''
      }));
    } catch (error) {
      // 프로필 업데이트 실패 - 사용자 정보 변경 문제 진단을 위해 로그 유지
      console.error('프로필 업데이트 실패:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : '프로필 업데이트에 실패했습니다.'
      }));
      throw error;
    }
  }, [state.user]);

  // 이메일 인증 코드 전송 함수
  const sendVerificationCode = useCallback(async (email: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: '' }));
      
      const guestToken = localStorage.getItem('guestToken');
      if (!guestToken) return;

      await authService.sendVerificationCode(guestToken, email);
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: ''
      }));
    } 
    catch (error) 
    {
      // 인증 이메일 전송 실패 - 이메일 전송 문제 진단을 위해 로그 유지
      console.error('인증 이메일 전송 실패:', error);

      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : '인증 이메일 전송에 실패했습니다.'
      }));
      
      throw error;
    }
  }, []);

  // 이메일 인증 코드 확인 함수
  const checkVerificationCode = useCallback(async (email: string, code: string) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: '' }));
      
      const guestToken = localStorage.getItem('guestToken');
      if (!guestToken) {
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: '인증 토큰이 없습니다.'
        }));
        return false; // 명시적으로 boolean 값 반환
      }
      
      const result = await authService.checkVerificationCode(guestToken, email, code);
      
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: ''
      }));
      
      return result;
    } catch (error) {
      // 이메일 인증 실패 - 인증 코드 검증 문제 진단을 위해 로그 유지
      console.error('이메일 인증 실패:', error);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : '이메일 인증에 실패했습니다.'
      }));
      throw error;
    }
  }, []);

  // 컨텍스트 값 메모이제이션
  const contextValue = useMemo(() => ({
    ...state,
    login,
    logout,
    signUp,
    updateProfile,
    sendVerificationCode,
    checkVerificationCode
  }), [
    state,
    login,
    logout,
    signUp,
    updateProfile,
    sendVerificationCode,
    checkVerificationCode
  ]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 